import React, { FC, ReactNode, useContext, useLayoutEffect, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useTour } from '@reactour/tour';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import { HeaderRight } from '../../../layout/Header/Header';
import api, { backendUrl } from '../../../custom/constants/urls';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { toast } from 'react-toastify';

dayjs.extend(relativeTime);

interface INotification {
    id: string;
    title: string;
    message: string;
    isRead: boolean;
    createdAt: string;
    userId?: string;
    updatedAt?: string;
}
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../../components/bootstrap/OffCanvas';
import Alert from '../../../components/bootstrap/Alert';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Icon from '../../../components/icon/Icon';
import ThemeContext from '../../../contexts/themeContext';
import LANG, { getLangWithKey, ILang } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import useDarkMode from '../../../hooks/useDarkMode';
import Popovers from '../../../components/bootstrap/Popovers';
import Spinner from '../../../components/bootstrap/Spinner';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../custom/redux/reduxHooks';
import {
	resetCustomer,
	resetCustomerBeneficiary,
	resetCustomerFilters,
} from '../../../custom/redux/reducers/customerManagementReducer';
import SocketStatusIndicator from '../../../custom/components/cashier/SocketStatusIndicator';

interface ICommonHeaderRightProps {
	beforeChildren?: ReactNode;
	afterChildren?: ReactNode;
}
const CommonHeaderRight: FC<ICommonHeaderRightProps> = ({ beforeChildren, afterChildren }) => {
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const [offcanvasStatus, setOffcanvasStatus] = useState(false);
	const [notifications, setNotifications] = useState<INotification[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 20,
		total: 0,
		totalPages: 0
	});

	const dummyNotifications: INotification[] = [];

	const fetchNotifications = async (page: number = 1) => {
		setIsLoading(true);
		try {
			const { data: response } = await api.get(
				`${backendUrl}/admin/get/admin/notifications?page=${page}&limit=${pagination.limit}`
			);
			
			if (response.success) {
				if (page === 1) {
					setNotifications(response.data || []);
				} else {
					setNotifications(prev => [...prev, ...(response.data || [])]);
				}
				setPagination(prev => ({
					...prev,
					page: response.pagination?.page || page,
					total: response.pagination?.total || 0,
					totalPages: response.pagination?.totalPages || 1
				}));
			} else {
				const errorMessage = response.message || 'Failed to load notifications';
				console.error('API returned error:', errorMessage);
				// toast.error(errorMessage);
				if (page === 1) {
					setNotifications([]);
				}
			}
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch notifications';
			console.error('Error fetching notifications:', errorMessage);
			// toast.error(errorMessage);
			
			if (page === 1) {
				setNotifications([]);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const markAsRead = async (notificationId: string) => {
		try {
			const { data: response } = await api.put(`${backendUrl}/admin/notifications/${notificationId}/read`);
			
			if (response.success) {
				setNotifications(prev => 
					prev.map(notif => 
						notif.id === notificationId 
							? { ...notif, isRead: true }
							: notif
					)
				);
			} else {
				const errorMessage = response.message || 'Failed to mark notification as read';
				console.error('API returned error:', errorMessage);
				toast.error(errorMessage);
			}
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || error.message || 'Failed to mark notification as read';
			console.error('Error marking notification as read:', errorMessage);
			toast.error(errorMessage);
			
			setNotifications(prev => 
				prev.map(notif => 
					notif.id === notificationId 
						? { ...notif, isRead: true }
						: notif
				)
			);
		}
	};

	const loadMoreNotifications = () => {
		if (pagination.page < pagination.totalPages && !isLoading) {
			fetchNotifications(pagination.page + 1);
		}
	};

	const refreshNotifications = () => {
		setPagination(prev => ({ ...prev, page: 1 }));
		fetchNotifications(1);
	};

	useEffect(() => {
		fetchNotifications(1);
		// const interval = setInterval(() => fetchNotifications(1), 30000);
		// return () => clearInterval(interval);
	}, []);

	const { i18n } = useTranslation();
	const dispatch = useAppDispatch();
	const changeLanguage = (lng: ILang['key']['lng']) => {
		i18n.changeLanguage(lng).then();
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(lng)?.text}`}</span>
			</span>,
			'You updated the language of the site. (Only "Aside" was prepared as an example.)',
		);
	};

	/**
	 * Language attribute
	 */
	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', i18n.language.substring(0, 2));
	});

	const { setIsOpen } = useTour();
	const navigate = useNavigate();

	return (
		<HeaderRight>
			<div className='header-right-container'>
				{beforeChildren}

				<SocketStatusIndicator />

				<div className='notifications-wrapper'>
					<div className="notification-button-container">
						<Button
							{...styledBtn}
							icon='Notifications'
							onClick={() => setOffcanvasStatus(true)}
							aria-label='Notifications'
							className='notification-btn'
						/>
						{notifications.filter(n => !n.isRead).length > 0 && (
							<div className="notification-badge">
								{notifications.filter(n => !n.isRead).length}
							</div>
						)}
					</div>
				</div>
				
				{afterChildren}
			</div>

			<style>
				{`
					.header-right-container {
						display: flex;
						align-items: center;
						gap: 1rem;
					}

					.notifications-wrapper {
						display: flex;
						align-items: center;
					}

					.notification-button-container {
						position: relative;
						display: flex;
						align-items: center;
					}

					.notification-btn {
						width: 2.5rem !important;
						height: 2.5rem !important;
						border-radius: 50% !important;
						display: flex !important;
						align-items: center !important;
						justify-content: center !important;
						transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
						background: var(--bs-info) !important;
						border: none !important;
						color: var(--bs-white) !important;
						box-shadow: 0 4px 15px rgba(var(--bs-info-rgb), 0.3) !important;
						position: relative !important;
						overflow: hidden !important;
					}

					.notification-btn::before {
						content: '';
						position: absolute;
						top: 0;
						left: -100%;
						width: 100%;
						height: 100%;
						background: linear-gradient(90deg, transparent, rgba(var(--bs-white-rgb), 0.2), transparent);
						transition: left 0.6s;
					}

					.notification-btn:hover {
						transform: translateY(-2px) scale(1.05) !important;
						box-shadow: 0 8px 25px rgba(var(--bs-info-rgb), 0.4) !important;
						color: var(--bs-white) !important;
					}

					.notification-btn:hover::before {
						left: 100%;
					}

					.notification-btn:active {
						transform: translateY(0) scale(0.98) !important;
					}

					.notification-badge {
						position: absolute;
						top: -4px;
						right: -4px;
						background: var(--bs-danger);
						color: var(--bs-white);
						border-radius: 50%;
						width: 1.25rem;
						height: 1.25rem;
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 0.7rem;
						font-weight: 600;
						border: 2px solid var(--bs-body-bg);
						z-index: 10;
						box-shadow: 0 2px 8px rgba(var(--bs-danger-rgb), 0.3);
						animation: pulse 2s infinite;
					}

					@keyframes pulse {
						0% {
							transform: scale(1);
						}
						50% {
							transform: scale(1.1);
						}
						100% {
							transform: scale(1);
						}
					}

					@media (max-width: 768px) {
						.header-right-container {
							gap: 0.75rem;
						}

						.notification-btn {
							width: 2.25rem !important;
							height: 2.25rem !important;
						}

						.notification-badge {
							width: 1.125rem;
							height: 1.125rem;
							font-size: 0.65rem;
							top: -1px;
							right: -1px;
						}
					}

					@media (max-width: 576px) {
						.header-right-container {
							gap: 0.5rem;
						}

						.notification-btn {
							width: 2rem !important;
							height: 2rem !important;
						}

						.notification-badge {
							width: 1rem;
							height: 1rem;
							font-size: 0.6rem;
						}
					}
				`}
			</style>

			<OffCanvas
				id='notificationCanvas'
				titleId='offcanvasExampleLabel'
				placement='end'
				isOpen={offcanvasStatus}
				setOpen={setOffcanvasStatus}>
				<OffCanvasHeader setOpen={setOffcanvasStatus}>
					<div className="d-flex align-items-center justify-content-between w-100">
						<OffCanvasTitle id='offcanvasExampleLabel'>Notifications</OffCanvasTitle>
						<Button
							color="primary"
							isLight
							size="sm"
							icon="Refresh"
							onClick={refreshNotifications}
							isDisable={isLoading}
							className="me-3"
						/>
					</div>
				</OffCanvasHeader>
				<OffCanvasBody>
					{isLoading ? (
						<div className="d-flex justify-content-center align-items-center h-32">
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : notifications.length === 0 ? (
						<div className="text-center text-muted p-4">
							<Icon icon="Notifications" size="3x" />
							<p className="mt-3">No notifications</p>
						</div>
					) : (
						<div className="notifications-list p-3">
							{notifications.map((notification) => (
								<div
									key={notification.id}
									className={classNames(
										'notification-item mb-3 rounded shadow-sm cursor-pointer transition-base',
										{
											'notification-unread': !notification.isRead,
											'notification-read': notification.isRead,
										}
									)}
									onClick={() => markAsRead(notification.id)}
								>
									<div className="d-flex p-3">
										<div className="notification-icon-wrapper me-3">
											<div className={classNames(
												'notification-icon-circle d-flex align-items-center justify-content-center',
												{
													'unread': !notification.isRead,
													'read': notification.isRead,
												}
											)}>
												<Icon 
													icon="CircleNotifications" 
													size="2x"
													className={classNames(
														'notification-icon',
														{ 'text-primary': !notification.isRead }
													)}
												/>
											</div>
										</div>
										<div className="notification-content flex-grow-1">
											<div className="d-flex justify-content-between align-items-start mb-1">
												<h6 className={classNames(
													'notification-title mb-0',
													{ 'fw-bold': !notification.isRead }
												)}>
													{notification.title}
												</h6>
												<small className="notification-time ms-2">
													{dayjs(notification.createdAt).format('MMM D, HH:mm')}
												</small>
											</div>
											<p className="notification-message mb-0">
												{notification.message}
											</p>
										</div>
									</div>
								</div>
							))}
							
							{/* Load More Button */}
							{pagination.page < pagination.totalPages && (
								<div className="text-center mt-3">
									<Button
										color="primary"
										isLight
										size="sm"
										onClick={loadMoreNotifications}
										isDisable={isLoading}
									>
										{isLoading ? (
											<>
												<div className="spinner-border spinner-border-sm me-2" role="status">
													<span className="visually-hidden">Loading...</span>
												</div>
												Loading...
											</>
										) : (
											'Load More'
										)}
									</Button>
								</div>
							)}
						</div>
					)}
					
					<style>
						{`
							/* Custom color utilities */
							.bg-dark-light {
								background-color: rgba(33, 37, 41, 0.1);
							}
							.bg-light-dark {
								background-color: rgba(248, 249, 250, 0.8);
							}
							.hover\\:bg-dark:hover {
								background-color: rgba(33, 37, 41, 0.2);
							}
							.hover\\:bg-light-dark:hover {
								background-color: rgba(248, 249, 250, 0.9);
							}
							.text-gray-300 {
								color: #dee2e6;
							}
							.text-gray-600 {
								color: #6c757d;
							}
							
							/* Notification Styles */
							.notifications-list {
								max-height: calc(100vh - 200px);
								overflow-y: auto;
							}
							.notification-item {
								background: #ffffff;
								border: 1px solid #e9ecef;
								transition: all 0.3s ease;
								border-radius: 0.5rem;
							}
							.notification-item:hover {
								transform: translateY(-2px);
								box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08);
							}
							.notification-unread {
								border-left: 4px solid var(--bs-primary);
							}
							.notification-read {
								border-left: 4px solid #e9ecef;
								background: #fafafa;
							}
							.notification-icon-circle {
								width: 40px;
								height: 40px;
								border-radius: 50%;
								background: #f8f9fa;
								transition: all 0.2s ease;
							}
							.notification-icon-circle.unread {
								background: rgba(var(--bs-primary-rgb), 0.1);
							}
							.notification-icon-circle.read {
								background: #f8f9fa;
							}
							.notification-icon {
								color: #6c757d;
							}
							.notification-time {
								color: #6c757d;
								font-size: 0.75rem;
							}
							.notification-message {
								color: #6c757d;
								font-size: 0.875rem;
								line-height: 1.4;
							}
							.notification-title {
								color: #212529;
								font-size: 0.95rem;
							}

							/* Dark mode adjustments */
							[data-bs-theme="dark"] .notification-item {
								background: rgba(33, 37, 41, 0.8);
								border-color: rgba(222, 226, 230, 0.1);
							}
							[data-bs-theme="dark"] .notification-read {
								background: rgba(33, 37, 41, 0.6);
							}
							[data-bs-theme="dark"] .notification-title {
								color: #f8f9fa;
							}
							[data-bs-theme="dark"] .notification-message {
								color: #dee2e6;
							}
							[data-bs-theme="dark"] .notification-time {
								color: #adb5bd;
							}
						`}
					</style>
				</OffCanvasBody>
			</OffCanvas>
		</HeaderRight>
	);
};
CommonHeaderRight.propTypes = {
	beforeChildren: PropTypes.node,
	afterChildren: PropTypes.node,
};
CommonHeaderRight.defaultProps = {
	beforeChildren: null,
	afterChildren: null,
};

export default CommonHeaderRight;
